import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Shield, Users, FileText, LayoutDashboard, Upload, ClipboardCheck, LogOut, Settings, FolderUp, ShieldAlert, Heart, CreditCard } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { GlowButton } from "@/components/ui/GlowButton";
import { NotificationBell } from "@/components/NotificationBell";
import { RoleSwitcher } from "@/components/layout/RoleSwitcher";

export const RoleBasedNavbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, signOut } = useAuth();
  const { isInternal, isSeller, isBuyer, isCeo } = useUserRole();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const getNavItems = () => {
    if (isInternal) {
      return [
        { name: "Dashboard", href: "/admin", icon: LayoutDashboard },
        { name: "Engagements", href: "/admin/engagements", icon: FileText },
        { name: "Reviews", href: "/admin/reviews", icon: ClipboardCheck },
        { name: "Bulk Import", href: "/admin/bulk-import", icon: FolderUp },
        { name: "Users", href: "/admin/users", icon: Users },
        { name: "Security", href: "/admin/security", icon: ShieldAlert },
        { name: "Settings", href: "/admin/settings", icon: Settings },
      ];
    }
    
    if (isCeo) {
      return [
        { name: "Dashboard", href: "/ceo", icon: Heart },
        { name: "Reports", href: "/ceo/reports", icon: FileText },
        { name: "Team", href: "/ceo/team", icon: Users },
        { name: "Billing", href: "/ceo/billing", icon: CreditCard },
        { name: "Settings", href: "/ceo/settings", icon: Settings },
      ];
    }
    
    if (isSeller) {
      return [
        { name: "Dashboard", href: "/seller", icon: LayoutDashboard },
        { name: "My engagements", href: "/seller/engagements", icon: FileText },
        { name: "Upload artefacts", href: "/seller/upload", icon: Upload },
      ];
    }
    
    if (isBuyer) {
      return [
        { name: "Dashboard", href: "/buyer", icon: LayoutDashboard },
        { name: "Reports", href: "/buyer/reports", icon: FileText },
      ];
    }

    // Default nav for users without specific roles
    return [
      { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
      { name: "Demo", href: "/demo", icon: FileText },
    ];
  };

  const navItems = getNavItems();

  return (
    <motion.nav
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary">
              <Shield className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="font-display text-xl font-bold tracking-tight">PROVENANCE</span>
            <RoleSwitcher />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center gap-2 text-sm transition-colors ${
                    isActive 
                      ? 'text-primary font-medium' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {item.name}
                </Link>
              );
            })}
          </div>

          {/* Actions */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                <NotificationBell />
                <span className="text-sm text-muted-foreground">
                  {user?.primaryEmailAddress?.emailAddress}
                </span>
                <GlowButton variant="ghost" size="sm" onClick={handleSignOut}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </GlowButton>
              </>
            ) : (
              <>
                <Link to="/auth">
                  <GlowButton variant="ghost" size="sm">Sign in</GlowButton>
                </Link>
                <Link to="/demo">
                  <GlowButton variant="primary" size="sm">Try demo</GlowButton>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-muted-foreground hover:text-foreground"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-background border-b border-border"
          >
            <div className="px-4 py-4 space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {item.name}
                  </Link>
                );
              })}
              
              <div className="pt-4 border-t border-border">
                {user ? (
                  <button
                    onClick={handleSignOut}
                    className="flex items-center gap-3 px-3 py-2 w-full text-left text-muted-foreground hover:bg-muted rounded-lg"
                  >
                    <LogOut className="w-5 h-5" />
                    Sign out
                  </button>
                ) : (
                  <Link
                    to="/auth"
                    onClick={() => setIsOpen(false)}
                    className="block px-3 py-2 text-center bg-primary text-primary-foreground rounded-lg"
                  >
                    Sign in
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};
